import React, { useState } from 'react';
import { Radio } from 'semantic-ui-react';
import { useSelector, useDispatch } from 'react-redux';
import FeatherIcon from 'feather-icons-react';
import CustomModal from 'src/components/configuracion/components/customModal';
import {
  CreateDocumentReport,
  ReportAction,
  ReportDocumentType,
  ReportReason,
  ReportReasonLabels,
} from 'src/graphql/documentReports';
import { importAlertState } from 'src/redux/actions/alert';
import { dt } from 'src/utils/documentsI18n';
import 'src/components/documentostributarios/ModalDeleteObligations.scss';
import './ReportDocumentModal.scss';

// Mismos tipos que `TIPOS_CON_ACCION` del backend (document-reports.logic.ts).
const TYPES_WITH_ACTION = [ReportDocumentType.TRANSACTION, ReportDocumentType.OBLIGATION];

const ReportDocumentModal = ({
  open,
  onClose,
  documentId,
  documentType,
  onReportSuccess,
  hasMatches = false,
  hasAsientos = false,
}) => {
  const dispatch = useDispatch();
  const [selectedReason, setSelectedReason] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [details, setDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { selectedCompany } = useSelector((state) => state.companyList);
  const { executeMutation: createReport } = CreateDocumentReport();

  const isBlocked = hasMatches || hasAsientos;
  const blockedTitle = hasMatches && hasAsientos
    ? dt('report.blockedTitleBoth')
    : hasAsientos
      ? dt('report.blockedTitleAsientos')
      : dt('report.blockedTitle');
  const blockedBody = hasMatches && hasAsientos
    ? dt('report.blockedBodyBoth')
    : hasAsientos
      ? dt('report.blockedBodyAsientos')
      : dt('report.blockedBody');

  const requiresAction = TYPES_WITH_ACTION.includes(documentType);
  const canSubmit = !!selectedReason && (!requiresAction || !!selectedAction);

  const reasons = Object.values(ReportReason).map((key) => ({ key, label: ReportReasonLabels[key] }));

  const actions = [
    {
      key: ReportAction.DELETE,
      label: documentType === ReportDocumentType.OBLIGATION
        ? dt('report.actionDeleteObligation')
        : dt('report.actionDelete'),
    },
    { key: ReportAction.REPORT_ONLY, label: dt('report.actionReportOnly') },
  ];

  const resetForm = () => {
    setSelectedReason(null);
    setSelectedAction(null);
    setDetails('');
    setError(null);
  };

  const handleClose = () => {
    if (loading) return;
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedReason) {
      setError(dt('report.selectReason'));
      return;
    }
    if (requiresAction && !selectedAction) {
      setError(dt('report.selectAction'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await createReport({
        input: {
          companyId: selectedCompany?.id,
          documentId,
          documentType,
          reason: selectedReason,
          action: requiresAction ? selectedAction : undefined,
          details: details.trim() || undefined,
        },
      });

      if (result.error) {
        throw new Error(result.error.graphQLErrors?.[0]?.message || result.error.message);
      }

      dispatch(
        importAlertState({
          state: 'success',
          content: dt('report.success'),
        })
      );

      if (onReportSuccess) {
        onReportSuccess(result.data.createDocumentReport);
      }

      resetForm();
      onClose();
    } catch (err) {
      setError(err.message || dt('report.error'));
    } finally {
      setLoading(false);
    }
  };

  const renderRadioGroup = (name, options, selected, onSelect, ariaLabel) => (
    <div
      className="delete-obligation-modal-summary report-radio-group"
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {options.map((option) => (
        <div
          key={option.key}
          className={`report-radio-option${selected === option.key ? ' is-selected' : ''}`}
          onClick={() => !loading && onSelect(option.key)}
        >
          <Radio
            name={name}
            label={option.label}
            value={option.key}
            checked={selected === option.key}
            onChange={() => onSelect(option.key)}
            disabled={loading}
          />
        </div>
      ))}
    </div>
  );

  return (
    <CustomModal
      open={open}
      onClose={handleClose}
      title={dt('report.title')}
      dimmer="blurring"
      classNameModal="delete-obligation-modal report-document-modal"
      classNameContent="delete-obligation-modal-content scfo-modal-form-body"
      closeOnDimmerClick={!loading}
      closeOnEscape={!loading}
      cancelarLabel={dt('common.cancel')}
      confirmLabel={isBlocked ? undefined : dt('report.report')}
      onConfirm={handleSubmit}
      confirmDisabled={!canSubmit || loading}
      isLoading={loading}
      confirmVariant={selectedAction === ReportAction.DELETE ? 'danger' : 'primary'}
      content={
        isBlocked ? (
          <div className="delete-obligation-modal-body">
            <div className="delete-obligation-modal-icon" aria-hidden>
              <FeatherIcon icon="flag" size={22} />
            </div>
            <p className="delete-obligation-modal-lead">
              <strong>{blockedTitle}</strong>
            </p>
            <p className="delete-obligation-modal-warning">{blockedBody}</p>
          </div>
        ) : (
          <div className="delete-obligation-modal-body report-document-form">
            <div className="delete-obligation-modal-icon" aria-hidden>
              <FeatherIcon icon="flag" size={22} />
            </div>

            <p className="delete-obligation-modal-lead">{dt('report.selectReason')}</p>
            {renderRadioGroup('report-reason', reasons, selectedReason, setSelectedReason, dt('report.reasonLabel'))}

            {requiresAction ? (
              <>
                <p className="report-section-title">{dt('report.actionTitle')}</p>
                {renderRadioGroup('report-action', actions, selectedAction, setSelectedAction, dt('report.actionTitle'))}
              </>
            ) : null}

            <textarea
              aria-label={dt('report.detailsLabel')}
              placeholder={dt('report.detailsPlaceholder')}
              value={details}
              maxLength={1000}
              rows={3}
              disabled={loading}
              onChange={(e) => setDetails(e.target.value)}
            />

            {error ? <div className="scfo-alert--error">{error}</div> : null}
          </div>
        )
      }
    />
  );
};

export default ReportDocumentModal;
